import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsSection } from './-components/settings-section';
import { can } from '@minty/shared/permissions';
import { Badge } from '~/components/ui/badge';
import { Skeleton } from '~/components/ui/skeleton';
import { authClient } from '~/lib/auth-client';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings/google-workspace')({
  validateSearch: (search): { result?: 'cancelled' | 'connected' | 'error' } =>
    search.result === 'cancelled' ||
    search.result === 'connected' ||
    search.result === 'error'
      ? { result: search.result }
      : {},
  pendingComponent: GoogleWorkspaceSettingsPending,
  component: GoogleWorkspaceSettingsRoute,
});

function GoogleWorkspaceSettingsRoute() {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const { data: currentUser } = useSuspenseQuery(
    trpc.auth.session.getCurrentUser.queryOptions(),
  );

  if (!can(currentUser.roles, 'integrations')) {
    return (
      <SettingsSection.Wrapper>
        <SettingsSection.Header>
          <SettingsSection.Title>{t('Google Workspace')}</SettingsSection.Title>
          <SettingsSection.Description>
            {t('You do not have permission to connect Google Workspace.')}
          </SettingsSection.Description>
        </SettingsSection.Header>
      </SettingsSection.Wrapper>
    );
  }

  return <GoogleWorkspaceConnection />;
}

function GoogleWorkspaceConnection() {
  const { t } = useTranslation('settings');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { mutateAsync: record } = useMutation(
    trpc.gmail.connection.record.mutationOptions(),
  );
  const { data: status } = useSuspenseQuery(
    trpc.gmail.connection.status.queryOptions(),
  );
  const callbackHandled = useRef(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isRecording, setIsRecording] = useState(search.result === 'connected');

  useEffect(() => {
    if (!search.result || callbackHandled.current) return;

    callbackHandled.current = true;

    const settle = async () => {
      if (search.result === 'connected') {
        // Better Auth has stored the grant against this admin's account; the
        // clinic-level record of which mailbox is connected is ours to write.
        try {
          await record();
          await queryClient.invalidateQueries({
            queryKey: trpc.gmail.connection.status.queryKey(),
          });
          notify.success(t('Google Workspace connected.'));
        } catch {
          notify.error(t('Unable to connect Google Workspace.'));
        } finally {
          setIsRecording(false);
        }
      } else {
        notify.error(
          search.result === 'cancelled'
            ? t('Google Workspace connection was cancelled.')
            : t('Unable to connect Google Workspace.'),
        );
      }

      await navigate({
        to: '/settings/google-workspace',
        search: {},
        replace: true,
      });
    };

    void settle();
  }, [navigate, queryClient, record, search.result, t, trpc]);

  async function handleConnect() {
    setIsStarting(true);

    try {
      const { data, error } = await authClient.oauth2.link({
        providerId: 'google-workspace',
        callbackURL: '/settings/google-workspace?result=connected',
        errorCallbackURL: '/settings/google-workspace?result=error',
      });

      if (error || !data.url) {
        throw new Error('link failed');
      }

      window.location.assign(data.url);
    } catch {
      notify.error(t('Unable to connect Google Workspace.'));
      setIsStarting(false);
    }
  }

  const isBusy = isStarting || isRecording;
  const available = status.configured && status.enabled;
  const connection = available ? status.connection : null;
  const statusPresentation = connection
    ? { label: t('Connected'), tone: 'success' as const }
    : available
      ? { label: t('Not connected'), tone: 'neutral' as const }
      : { label: t('Not configured'), tone: 'warning' as const };

  return (
    <SettingsSection.Wrapper aria-busy={isBusy}>
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Google Workspace')}</SettingsSection.Title>
        <SettingsSection.Description>
          {t(
            'Connect the clinic mailbox so MintyAssistant can mark handled emails in Gmail.',
          )}
        </SettingsSection.Description>
      </SettingsSection.Header>

      <SettingsSection.ContentWrapper
        className={available ? undefined : 'border-b-0'}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-foreground">
            {connection
              ? connection.mailboxAddress
              : available
                ? t('No Google Workspace mailbox is connected.')
                : t('Google OAuth is not configured on this deployment.')}
          </p>
          <Badge tone={statusPresentation.tone}>
            {statusPresentation.label}
          </Badge>
        </div>
      </SettingsSection.ContentWrapper>

      {available && (
        <SettingsSection.Footer className="justify-end">
          <SettingsSection.FooterButton
            type="button"
            disabled={isBusy}
            onClick={() => void handleConnect()}
          >
            {isBusy && <Loader2 className="animate-spin" strokeWidth={2.5} />}
            {connection
              ? t('Reconnect Google Workspace')
              : t('Connect Google Workspace')}
          </SettingsSection.FooterButton>
        </SettingsSection.Footer>
      )}
    </SettingsSection.Wrapper>
  );
}

function GoogleWorkspaceSettingsPending() {
  const { t } = useTranslation('settings');

  return (
    <SettingsSection.Wrapper aria-busy="true">
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Google Workspace')}</SettingsSection.Title>
        <SettingsSection.Description>
          {t(
            'Connect the clinic mailbox so MintyAssistant can mark handled emails in Gmail.',
          )}
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.ContentWrapper>
        <Skeleton className="h-8 w-full" />
      </SettingsSection.ContentWrapper>
      <SettingsSection.Footer className="justify-end">
        <SettingsSection.FooterButton type="button" disabled>
          {t('Connect Google Workspace')}
        </SettingsSection.FooterButton>
      </SettingsSection.Footer>
    </SettingsSection.Wrapper>
  );
}
